import { equivalentUnitKey, UnitOfMeasureOption } from './sage-integration.model';

/** Where a unit was found in a name: the number in front (if any) and the unit, for highlighting. */
export interface UnitNameHit {
  start: number;
  end: number;
  quantity: number | null;
}

export interface UnitSuggestion {
  unit: UnitOfMeasureOption;
  hits: UnitNameHit[];
}

// In an item's name these describe the item (a thickness, a diameter), not how it is bought.
const SIZE_UNIT_KEYS = new Set(['mm', 'cm', 'mm2', 'cm2']);
const LENGTH_UNIT_KEYS = new Set(['m', 'm2']);
export const METER_MIN = 10;

interface Hit extends UnitNameHit {
  unit: UnitOfMeasureOption;
  unitStart: number;
}

// Keeps every character in place, so positions found here are positions in the name.
function fold(value: string): string {
  return value.toLowerCase().replace(/²/g, '2').replace(/³/g, '3');
}

const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);
const isLetter = (c: string | undefined) => !!c && /\p{L}/u.test(c);
const isDigit = (c: string | undefined) => !!c && c >= '0' && c <= '9';

interface Token {
  text: string;
  isCode: boolean;
}

function tokensFor(unit: UnitOfMeasureOption): Token[] {
  const tokens: Token[] = [];
  const code = fold(unit.code.trim());
  if (code) tokens.push({ text: code, isCode: true });
  const name = fold(unit.name.trim());
  if (name) {
    tokens.push({ text: name, isCode: false });
    const british = name.replace(/liter/g, 'litre').replace(/meter/g, 'metre');
    if (british !== name) tokens.push({ text: british, isCode: false });
  }
  return tokens;
}

function numberBefore(text: string, index: number): { start: number; value: number } | null {
  let end = index;
  if (text[end - 1] === ' ') end -= 1;
  if (!isDigit(text[end - 1])) return null;
  let start = end - 1;
  while (start > 0 && (isDigit(text[start - 1]) || ((text[start - 1] === '.' || text[start - 1] === ',') && isDigit(text[start - 2])))) {
    start -= 1;
  }
  return { start, value: Number(text.slice(start, end).replace(',', '.')) };
}

// "2x3m", "2 x 1.5m²", "3m x 2m": one side of a size, not something bought by the metre.
function isDimension(text: string, start: number, end: number): boolean {
  const before = text.slice(0, start).match(/(\d)\s*[x×]\s*$/);
  const after = text.slice(end).match(/^\s*[x×]\s*\d/);
  return !!before || !!after;
}

function findHits(name: string, units: UnitOfMeasureOption[]): Hit[] {
  const text = fold(name);
  const hits: Hit[] = [];
  for (const unit of units) {
    for (const token of tokensFor(unit)) {
      const pluralOk = !token.isCode || token.text.length > 1;
      for (let from = 0; ; ) {
        const at = text.indexOf(token.text, from);
        if (at < 0) break;
        from = at + 1;
        let end = at + token.text.length;
        if (pluralOk && text[end] === 's' && !isWordChar(text[end + 1])) end += 1;
        // Whole words only: "hammer" isn't mm, "12mm" isn't m.
        if (isWordChar(text[end])) continue;
        const number = numberBefore(text, at);
        // A bare code is too short to trust ("Size M", a lone "g"), so it needs a number in front.
        if (token.isCode ? !number : isLetter(text[at - 1])) continue;
        hits.push({ unit, unitStart: at, start: number?.start ?? at, end, quantity: number?.value ?? null });
      }
    }
  }
  // The longest unit wins where two overlap: "4m²" is m², not m.
  hits.sort((a, b) => b.end - b.unitStart - (a.end - a.unitStart));
  const kept: Hit[] = [];
  for (const hit of hits) {
    if (!kept.some((k) => hit.start < k.end && k.start < hit.end)) kept.push(hit);
  }
  return kept.sort((a, b) => a.start - b.start);
}

function isSize(hit: Hit, text: string): boolean {
  const key = equivalentUnitKey(hit.unit.code);
  if (SIZE_UNIT_KEYS.has(key)) return true;
  if (!LENGTH_UNIT_KEYS.has(key)) return false;
  if (isDimension(text, hit.start, hit.end)) return true;
  return key === 'm' && (hit.quantity ?? 0) < METER_MIN;
}

/** The one Aivor unit an item's name names, such as Kilogram for "Espresso beans 1kg", or null
 * when it names none, only sizes, or two different units. */
export function suggestUnitFromName(name: string, units: UnitOfMeasureOption[]): UnitSuggestion | null {
  if (!name || !units.length) return null;
  const text = fold(name);
  const hits = findHits(name, units).filter((h) => !isSize(h, text));
  const ids = new Set(hits.map((h) => h.unit.id));
  if (ids.size !== 1) return null;
  return { unit: hits[0].unit, hits: hits.map(({ start, end, quantity }) => ({ start, end, quantity })) };
}
